import { AnalyticsService } from 'base/analytics';
import { exists, keyOfObject } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Button } from 'base/ui/button';
import { IconAdd, IconHome, IconOption, IconPage } from 'base/ui/icons';
import { Inline, SpacerGrow } from 'base/ui/layout';
import { MenuButton } from 'base/ui/menu';
import { menuItem, MenuModel } from 'base/ui/menu_model';
import { ClickInTextarea, ClickInTextareaRef } from 'base/ui/textarea';
import { TextSmall, TextTinyLight } from 'base/ui/typography';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { NotificationsContainer, TaskNotifications } from '../ai/notifications';
import { ProjectEditingActions } from '../editing_actions/project';
import { EditorMode } from '../editor_mode';
import { Page, PageState, Project, Space } from '../model';
import { pageAndAllSubpages, unrefPresentSections } from '../model_helpers';
import { useSelectable } from '../selection/selectable';
import { selectionLocale, SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { ProjectSession } from '../session/session';
import { useCommonPageInteractions } from '../sitemap/page_common';
import { pageDndDomain } from '../sitemap/page_dnd';
import { ConceptAPI } from '../styleguide/concept_store';
import { StyledElementsContext } from '../styleguide/styled_element_view';
import { globalSuggestionsStore } from '../suggested_edits';
import {
  breakpointStore,
  BREAKPOINT_SIZE,
  BREAKPOINT_TO_CANVAS_SCALE,
} from './breakpoint';
import { fillInMissingComponents } from './fill_in_missing_components';
import styles from './pages.css';
import { PageSectionsTail, PageSectionsView } from './section';

export const WireframePages = observer(function _WireframePages({
  selection,
  selectionActions,
  analytics,
  session,
  editing,
  library,
  concept,
}: {
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  session: ProjectSession;
  editing: ProjectEditingActions | undefined;
  library: LibraryCache;
  concept?: ConceptAPI;
}) {
  const space = selection.get().space || 'home';
  const locale = selectionLocale({ space }, selection.project);

  React.useEffect(() => {
    if (!editing) return;
    const abort = new AbortController();
    fillInMissingComponents(
      session.project,
      space,
      editing.assistant,
      session,
      abort.signal,
    );
    return () => abort.abort();
  }, [editing, session, space]);

  const renderPageSections = React.useCallback(
    (page: Page | PageState) => (
      <PageSectionsView
        page={page}
        selection={selection}
        selectionActions={selectionActions}
        analytics={analytics}
        session={session}
        editing={editing}
        library={library}
        concept={concept}
      />
    ),
    [
      selection,
      selectionActions,
      analytics,
      session,
      editing,
      library,
      concept,
    ],
  );

  const renderPage = React.useCallback(
    ({
      page,
      path,
      isHomePage,
    }: {
      page: Page | PageState;
      path: string;
      isHomePage: boolean;
    }) => {
      const fullPath = `${path}/${
        // "Home" page path is just "/"
        isHomePage ? '' : pageNameOrUntitled(page.name)
      }`;
      return (
        <PageView
          page={page}
          isHomePage={isHomePage}
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          editing={editing}
          renderPageSections={renderPageSections}
          flags={session.flags}
          path={fullPath}
        />
      );
    },
    [
      selection,
      selectionActions,
      analytics,
      editing,
      renderPageSections,
      session.flags,
    ],
  );

  // If we are on the wireframe page with a path selected, we move focus to it's first child,
  // as paths are not rendered outside of the sitemap.
  const sel = selection.get();
  if (sel.page?.pageType === 'path') {
    const firstNonPathChild = pageAndAllSubpages(sel.page).find(
      (p) => p.pageType !== 'path',
    );
    const newSelection = firstNonPathChild
      ? selection.factory.forPage(firstNonPathChild)
      : selection.factory.treeParentOfPage(sel.page);
    selection.set(newSelection);
  }
  const parent = space === 'home' ? selection.project.homePage : space;
  return (
    <div className={styles.canvas} lang={locale}>
      {space === 'home' &&
        renderPage({
          page: selection.project.homePage,
          path: '',
          isHomePage: true,
        })}
      <PageListView
        project={selection.project}
        parent={parent}
        editing={editing}
        renderPage={renderPage}
        flags={session.flags}
        path={''}
        concept={concept}
        library={library}
      />
    </div>
  );
});

const PageListView = observer(function _PageListView({
  project,
  parent,
  editing,
  renderPage,
  flags,
  path,
  concept,
  library,
}: {
  project: Project;
  parent: Page | Space;
  editing: ProjectEditingActions | undefined;
  renderPage: (opts: {
    page: Page | PageState;
    path: string;
    isHomePage: boolean;
  }) => React.ReactNode;
  flags: FeatureFlagsStore;
  path: string;
  concept?: ConceptAPI;
  library: LibraryCache;
}) {
  const renderDragPreview = React.useMemo(() => {
    return concept != null
      ? ({ children }: { children: React.ReactNode }) => (
          <StyledElementsContext concept={concept} library={library}>
            {children}
          </StyledElementsContext>
        )
      : undefined;
  }, [concept, library]);

  const suggestedSubpages = globalSuggestionsStore.getSuggestedPages(parent);
  function renderActualPage(index: number) {
    const page = parent.subPages[index];

    function renderChildPages() {
      return (
        <PageListView
          project={project}
          parent={page}
          editing={editing}
          renderPage={renderPage}
          flags={flags}
          path={`${path}/${pageNameOrUntitled(page.name)}`}
          concept={concept}
          library={library}
        />
      );
    }
    // Paths have no visual representation in the wireframe, so we just render
    // its children.
    if (page.pageType === 'path') return renderChildPages();
    return (
      <React.Fragment key={keyOfObject(page)}>
        <pageDndDomain.Draggable
          source={
            editing
              ? {
                  page: page.state,
                  from: { parent, index, mutable: page },
                }
              : undefined
          }
          supportsDuplicate={true}
          selfDropTarget={{ parent, index }}
          onAfterDrop={editing?.onAfterDropPage}
          dropTarget={
            editing && {
              mouseToDestination: (clientX, _, el) => {
                const box = el.getBoundingClientRect();
                const isLeft = clientX <= (box.left + box.right) / 2;
                return { parent, index: index + (isLeft ? 0 : 1) };
              },
              processDrop: editing.dropPageAt,
            }
          }
          renderDragPreview={renderDragPreview}
        >
          {renderPage({ page, path, isHomePage: isHomePage(project, page) })}
        </pageDndDomain.Draggable>
        {renderChildPages()}
      </React.Fragment>
    );
  }

  function renderDropPreview(index: number) {
    const dndState = pageDndDomain.state.get();
    if (dndState?.dest?.parent !== parent || dndState?.dest.index !== index) {
      return;
    }
    return (
      <pageDndDomain.DropTarget
        opts={
          editing && {
            mouseToDestination: () => ({ parent, index }),
            processDrop: editing.dropPageAt,
          }
        }
        key={`drop-${index}`}
        className={styles.dropPreview}
      >
        <div className={styles.dropPreviewBox}>
          <div className={styles.dropPreviewInner}>
            {renderPage({
              page: dndState.source.page,
              path,
              isHomePage: false,
            })}
          </div>
        </div>
      </pageDndDomain.DropTarget>
    );
  }

  function renderSuggestions(index: number) {
    const pages = suggestedSubpages
      .filter((s) => s.index === index)
      .map((s) => s.page);
    if (pages.length === 0) return null;

    return pages.map((page) => (
      <pageDndDomain.DropTarget
        opts={
          editing && {
            mouseToDestination: () => ({ parent, index }),
            processDrop: editing.dropPageAt,
          }
        }
        key={keyOfObject(page)}
        className={styles.pageContainerIsSuggested}
      >
        {renderPage({ page, path, isHomePage: false })}
      </pageDndDomain.DropTarget>
    ));
  }
  return (
    <>
      {Array.from({ length: parent.subPages.length }).flatMap((_, index) => [
        renderDropPreview(index),
        renderSuggestions(index),
        renderActualPage(index),
      ])}
      {renderDropPreview(parent.subPages.length)}
      {renderSuggestions(parent.subPages.length)}
    </>
  );
});

export const PageView = observer(function _PageView({
  page,
  isHomePage,
  selection,
  selectionActions,
  analytics,
  editing,
  renderPageSections,
  flags,
  path,
}: {
  page: Page | PageState;
  isHomePage: boolean;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  renderPageSections: (page: Page | PageState) => React.ReactNode;
  flags: FeatureFlagsStore;
  path: string;
}) {
  const pageEditing = page.mutable ? editing?.forPage(page) : undefined;
  const breakpointWidth = BREAKPOINT_SIZE[breakpointStore.get()].width;
  const columnWidth = breakpointWidth * BREAKPOINT_TO_CANVAS_SCALE;
  const topBarRef = React.useRef<HTMLDivElement>(null);
  const pageSectionsContainerRef = React.useRef<HTMLDivElement>(null);
  const [isSelected, selectableClassName] = page.mutable
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useSelectable(topBarRef, selection, selection.factory.forPage(page), {
        onSelect: editing?.sidebarNavigation.openEditPanelIfClosed,
        attachFrameTo: pageSectionsContainerRef,
      })
    : [false, ''];
  const [menu, onContextMenu, menuPortal] = useCommonPageInteractions({
    selectionActions,
    analytics,
    projectEditing: editing,
    pageEditing,
    isSelected,
    showPathOption: false,
    extraMenuItems: pageEditing && [
      menuItem({
        label: 'Reset copy',
        disabled: pageEditing?.resetCopy == null,
        onClick:
          pageEditing?.resetCopy != null
            ? () => {
                pageEditing.resetCopy!();
                analytics.track({
                  event: 'blocks/reset',
                  object: 'copy',
                  subject: 'page',
                  componentSlugs: (page.mutable
                    ? unrefPresentSections(page.sections, selection.project)
                    : unrefPresentSections(
                        page.sections,
                        selection.project.state,
                      )
                  )
                    .map((s) => s.element.current?.source.slug)
                    .filter(exists),
                });
              }
            : undefined,
        tooltip:
          pageEditing?.resetCopy == null
            ? 'You haven’t changed the copy'
            : undefined,
      }),
    ],
  });

  return (
    <div
      className={styles.page}
      style={{ width: columnWidth }}
      onContextMenu={onContextMenu}
    >
      {pageEditing?.addPageBefore && (
        <div className={classNames(styles.addPage, styles.isBefore)}>
          <Button
            icon={IconAdd}
            variant="tertiary"
            background="grey2"
            onClick={() => {
              pageEditing.addPageBefore!();
              analytics.track({
                event: 'blocks/add-page',
                method: 'blank',
                direction: 'before',
              });
            }}
          />
        </div>
      )}
      {pageEditing?.addPageAfter && (
        <div className={classNames(styles.addPage, styles.isAfter)}>
          <Button
            icon={IconAdd}
            variant="tertiary"
            background="grey2"
            onClick={() => {
              pageEditing.addPageAfter!();
              analytics.track({
                event: 'blocks/add-page',
                method: 'blank',
                direction: 'after',
              });
            }}
          />
        </div>
      )}
      <div className={styles.topBarAndPageSectionContainer}>
        <PageTopBar
          refDiv={topBarRef}
          page={page}
          isHomePage={isHomePage}
          menu={menu}
          editing={editing}
          isSelected={isSelected}
          selectableClassName={selectableClassName}
          flags={flags}
          path={path}
        />
        <div ref={pageSectionsContainerRef}>{renderPageSections(page)}</div>
      </div>
      <PageSectionsTail page={page} editing={editing} />
      {menuPortal}
    </div>
  );
});

const PageTopBar = observer(function _PageTopBar({
  refDiv,
  page,
  isHomePage,
  menu,
  editing,
  selectableClassName,
  isSelected,
  flags,
  path,
}: {
  refDiv: React.RefObject<HTMLDivElement>;
  page: Page | PageState;
  isHomePage: boolean;
  menu: MenuModel;
  editing: ProjectEditingActions | undefined;
  selectableClassName: string;
  isSelected: boolean;
  flags: FeatureFlagsStore;
  path: string;
}) {
  const nameRef = React.useRef<ClickInTextareaRef>(null);
  const pageEditing = page.mutable ? editing?.forPage(page) : undefined;
  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter')]: () => nameRef.current?.focus(),
  });

  // TODO(meredith): Move this into Textarea, or make the textfield a form input.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.stopPropagation();
      e.preventDefault();
      e.currentTarget.blur();
    }
  };
  return (
    <div
      ref={refDiv}
      className={classNames(
        styles.pageTopBar,
        selectableClassName,
        isSelected && styles.isSelected,
      )}
    >
      <Inline spacing={8} yalign="center">
        <div className={styles.pageTopBarIcon}>
          {isHomePage ? <IconHome /> : <IconPage />}
        </div>
        <TextSmall>
          <ClickInTextarea
            ref={nameRef}
            enableClickIn={isSelected}
            value={page.name}
            onChange={pageEditing?.setName}
            placeholder="Untitled page"
            onKeyDown={onKeyDown}
          />
        </TextSmall>
        <div className={styles.pathContainer}>
          <PathName path={path} />
        </div>
        <SpacerGrow />
        {menu.length > 0 && (
          <MenuButton icon={IconOption} menu={menu} variant="tertiary" />
        )}
      </Inline>
      <NotificationsContainer offset="wireframe">
        {editing && page.mutable && (
          <TaskNotifications
            editing={editing}
            target={page}
            mode={EditorMode.Wireframe}
          />
        )}
      </NotificationsContainer>
    </div>
  );
});

export const PathName = ({ path }: { path: string }) => {
  const pathParts = path.split('/');
  return (
    <Inline spacing={4}>
      {pathParts.map((part, i) => (
        <Inline spacing={4} key={i}>
          <TextTinyLight color="black">{part}</TextTinyLight>
          {i < pathParts.length - 1 && (
            <TextTinyLight color="black">/</TextTinyLight>
          )}
        </Inline>
      ))}
    </Inline>
  );
};

function isHomePage(project: Project, page: Page) {
  if (project.homePage === page) {
    return true;
  }
  // The first page of every space is considered the "home" page.
  return project.spaces.some(
    (space: Space) => space.subPages.length > 0 && space.subPages[0] === page,
  );
}

function pageNameOrUntitled(pageName: string) {
  return pageName !== '' ? pageName : 'Untitled page';
}
